// Chemin: C:\gestion-copro\src\components\extraction\ExtractionValidationModal.tsx

import React, { useState, useEffect } from 'react';
import {
  ExtractedLot,
  ExtractedMembreCS,
  LotsExtractionResult,
  CSExtractionResult,
  generateTempId,
} from '../../types/extraction.types';

// ============================================
// FONCTIONS UTILITAIRES
// ============================================

const calculateTotalTantiemes = (lots: ExtractedLot[]): number => {
  return lots
    .filter((lot) => lot.selected)
    .reduce((total, lot) => total + (lot.tantiemesGeneraux || 0), 0);
};

const countSelectedItems = <T extends { selected: boolean }>(items: T[]): number => {
  return items.filter((item) => item.selected).length;
};

// ============================================
// TYPES
// ============================================

interface ExtractionValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmLots?: (items: ExtractedLot[]) => void;
  onConfirmMembresCS?: (items: ExtractedMembreCS[]) => void;
  lotsResult?: LotsExtractionResult;
  csResult?: CSExtractionResult;
  isImporting: boolean;
  availableLots?: Array<{ id: string; numero: string }>;
}

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export const ExtractionValidationModal: React.FC<ExtractionValidationModalProps> = ({
  isOpen,
  onClose,
  onConfirmLots,
  onConfirmMembresCS,
  lotsResult,
  csResult,
  isImporting,
  availableLots = [],
}) => {
  const [lots, setLots] = useState<ExtractedLot[]>([]);
  const [membresCS, setMembresCS] = useState<ExtractedMembreCS[]>([]);
  const [selectAll, setSelectAll] = useState(true);

  const isLots = !!lotsResult;
  const result = lotsResult || csResult;

  // Initialiser les items quand le résultat change
  useEffect(() => {
    if (lotsResult?.items) {
      setLots([...lotsResult.items]);
      setSelectAll(lotsResult.items.every((item) => item.selected));
    }
  }, [lotsResult]);

  useEffect(() => {
    if (csResult?.items) {
      setMembresCS([...csResult.items]);
      setSelectAll(csResult.items.every((item) => item.selected));
    }
  }, [csResult]);

  if (!isOpen || !result) return null;

  // ============================================
  // HANDLERS LOTS
  // ============================================

  const handleSelectAllLots = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setLots(lots.map((item) => ({ ...item, selected: newSelectAll })));
  };

  const handleToggleSelectLot = (tempId: string) => {
    setLots(
      lots.map((item) =>
        item.tempId === tempId ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleDeleteLot = (tempId: string) => {
    setLots(lots.filter((item) => item.tempId !== tempId));
  };

  const handleUpdateLot = (tempId: string, field: keyof ExtractedLot, value: string | number) => {
    setLots(
      lots.map((item) =>
        item.tempId === tempId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleAddLot = () => {
    const newLot: ExtractedLot = {
      tempId: generateTempId(),
      numero: '',
      type: 'Logement',
      etage: '',
      tantiemesGeneraux: 0,
      selected: true,
      hasError: true,
      errorMessage: 'Nouveau lot - à compléter',
    };
    setLots([...lots, newLot]);
  };

  // ============================================
  // HANDLERS MEMBRES CS
  // ============================================

  const handleSelectAllMembresCS = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setMembresCS(membresCS.map((item) => ({ ...item, selected: newSelectAll })));
  };

  const handleToggleSelectMembreCS = (tempId: string) => {
    setMembresCS(
      membresCS.map((item) =>
        item.tempId === tempId ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleDeleteMembreCS = (tempId: string) => {
    setMembresCS(membresCS.filter((item) => item.tempId !== tempId));
  };

  const handleUpdateMembreCS = (tempId: string, field: keyof ExtractedMembreCS, value: string) => {
    setMembresCS(
      membresCS.map((item) =>
        item.tempId === tempId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleAddMembreCS = () => {
    const newMembre: ExtractedMembreCS = {
      tempId: generateTempId(),
      nom: '',
      role: 'membre',
      selected: true,
      hasError: true,
      errorMessage: 'Nouveau membre - à compléter',
    };
    setMembresCS([...membresCS, newMembre]);
  };

  // ============================================
  // CONFIRM
  // ============================================

  const handleConfirm = () => {
    if (isLots) {
      const selectedItems = lots.filter((item) => item.selected);
      if (selectedItems.length === 0) {
        alert('Veuillez sélectionner au moins un lot à importer.');
        return;
      }
      onConfirmLots?.(selectedItems);
    } else {
      const selectedItems = membresCS.filter((item) => item.selected);
      if (selectedItems.length === 0) {
        alert('Veuillez sélectionner au moins un membre à importer.');
        return;
      }
      onConfirmMembresCS?.(selectedItems);
    }
  };

  // ============================================
  // CALCULS
  // ============================================

const selectedCount = isLots ? countSelectedItems(lots) : countSelectedItems(membresCS);
const totalCount = isLots ? lots.length : membresCS.length;
const totalTantiemes = isLots ? calculateTotalTantiemes(lots) : 0;
  // ============================================
  // RENDU
  // ============================================

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isLots ? 'Lots extraits' : 'Membres du Conseil Syndical extraits'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Document : {result.documentName}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Badge de confiance */}
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  result.confidence === 'high'
                    ? 'bg-green-100 text-green-800'
                    : result.confidence === 'medium'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                Confiance : {result.confidence === 'high' ? 'Haute' : result.confidence === 'medium' ? 'Moyenne' : 'Basse'}
              </span>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
                disabled={isImporting}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Warnings */}
          {result.warnings.length > 0 && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Attention :</strong> {result.warnings.join(' - ')}
              </p>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-6">
          {isLots ? (
            <LotsTable
              lots={lots}
              selectAll={selectAll}
              onSelectAll={handleSelectAllLots}
              onToggleSelect={handleToggleSelectLot}
              onUpdate={handleUpdateLot}
              onDelete={handleDeleteLot}
            />
          ) : (
            <MembresCSTable
              membres={membresCS}
              selectAll={selectAll}
              onSelectAll={handleSelectAllMembresCS}
              onToggleSelect={handleToggleSelectMembreCS}
              onUpdate={handleUpdateMembreCS}
              onDelete={handleDeleteMembreCS}
              availableLots={availableLots}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button
                onClick={isLots ? handleAddLot : handleAddMembreCS}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                disabled={isImporting}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {isLots ? 'Ajouter un lot' : 'Ajouter un membre'}
              </button>
              <span className="text-sm text-gray-600">
        {selectedCount} / {totalCount} sélectionné(s)              </span>
              {isLots && (
                <span className="text-sm text-gray-600">
                  Total tantièmes : <strong>{totalTantiemes.toLocaleString('fr-FR')}</strong>
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isImporting}
              >
                Annuler
              </button>
              <button
                onClick={handleConfirm}
                disabled={isImporting || selectedCount === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isImporting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Import en cours...
                  </>
                ) : (
                  <>Importer {selectedCount} {isLots ? 'lot(s)' : 'membre(s)'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SOUS-COMPOSANT : TABLE DES LOTS
// ============================================

interface LotsTableProps {
  lots: ExtractedLot[];
  selectAll: boolean;
  onSelectAll: () => void;
  onToggleSelect: (tempId: string) => void;
  onUpdate: (tempId: string, field: keyof ExtractedLot, value: string | number) => void;
  onDelete: (tempId: string) => void;
}

const LotsTable: React.FC<LotsTableProps> = ({
  lots,
  selectAll,
  onSelectAll,
  onToggleSelect,
  onUpdate,
  onDelete,
}) => {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-gray-100">
          <th className="px-3 py-2 text-left w-10">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={onSelectAll}
              className="rounded border-gray-300"
            />
          </th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Lot</th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Type</th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Etage</th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Description</th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Tantièmes</th>
          <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 w-20">Actions</th>
        </tr>
      </thead>
      <tbody>
        {lots.map((lot) => (
          <tr
            key={lot.tempId}
            className={`border-b border-gray-200 ${
              lot.hasError ? 'bg-red-50' : lot.selected ? 'bg-white' : 'bg-gray-50'
            }`}
          >
            <td className="px-3 py-2">
              <input
                type="checkbox"
                checked={lot.selected}
                onChange={() => onToggleSelect(lot.tempId)}
                className="rounded border-gray-300"
              />
            </td>
            <td className="px-3 py-2">
              <input
                type="text"
                value={lot.numero}
                onChange={(e) => onUpdate(lot.tempId, 'numero', e.target.value)}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="N°"
              />
            </td>
            <td className="px-3 py-2">
              <select
                value={lot.type}
                onChange={(e) => onUpdate(lot.tempId, 'type', e.target.value)}
                className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="T1">T1</option>
                <option value="T2">T2</option>
                <option value="T3">T3</option>
                <option value="T4">T4</option>
                <option value="T5">T5+</option>
                <option value="Studio">Studio</option>
                <option value="Logement">Logement</option>
              </select>
            </td>
            <td className="px-3 py-2">
              <input
                type="text"
                value={lot.etage}
                onChange={(e) => onUpdate(lot.tempId, 'etage', e.target.value)}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="Etage"
              />
            </td>
            <td className="px-3 py-2">
              <input
                type="text"
                value={lot.description || ''}
                onChange={(e) => onUpdate(lot.tempId, 'description', e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="Description"
              />
            </td>
            <td className="px-3 py-2">
              <input
                type="number"
                value={lot.tantiemesGeneraux}
                onChange={(e) => onUpdate(lot.tempId, 'tantiemesGeneraux', parseInt(e.target.value) || 0)}
                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                placeholder="0"
              />
            </td>
            <td className="px-3 py-2 text-center">
              <button
                onClick={() => onDelete(lot.tempId)}
                className="text-red-500 hover:text-red-700"
                title="Supprimer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ============================================
// SOUS-COMPOSANT : TABLE DES MEMBRES CS
// ============================================

interface MembresCSTableProps {
  membres: ExtractedMembreCS[];
  selectAll: boolean;
  onSelectAll: () => void;
  onToggleSelect: (tempId: string) => void;
  onUpdate: (tempId: string, field: keyof ExtractedMembreCS, value: string) => void;
  onDelete: (tempId: string) => void;
  availableLots: Array<{ id: string; numero: string }>;
}

const MembresCSTable: React.FC<MembresCSTableProps> = ({
  membres,
  selectAll,
  onSelectAll,
  onToggleSelect,
  onUpdate,
  onDelete,
  availableLots,
}) => {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-gray-100">
          <th className="px-3 py-2 text-left w-10">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={onSelectAll}
              className="rounded border-gray-300"
            />
          </th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Nom</th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Prénom</th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Fonction</th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Lot</th>
          <th className="px-3 py-2 text-left text-sm font-medium text-gray-700">Email</th>
          <th className="px-3 py-2 text-center text-sm font-medium text-gray-700 w-20">Actions</th>
        </tr>
      </thead>
      <tbody>
        {membres.map((membre) => (
          <tr
            key={membre.tempId}
            className={`border-b border-gray-200 ${
              membre.hasError ? 'bg-red-50' : membre.selected ? 'bg-white' : 'bg-gray-50'
            }`}
          >
            <td className="px-3 py-2">
              <input
                type="checkbox"
                checked={membre.selected}
                onChange={() => onToggleSelect(membre.tempId)}
                className="rounded border-gray-300"
              />
            </td>
            <td className="px-3 py-2">
              <input
                type="text"
                value={membre.nom}
                onChange={(e) => onUpdate(membre.tempId, 'nom', e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="NOM"
              />
            </td>
            <td className="px-3 py-2">
              <input
                type="text"
                value={membre.prenom || ''}
                onChange={(e) => onUpdate(membre.tempId, 'prenom', e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="Prénom"
              />
            </td>
            <td className="px-3 py-2">
              <select
                value={membre.role}
                onChange={(e) => onUpdate(membre.tempId, 'role', e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="president">Président</option>
                <option value="vice_president">Vice-président</option>
                <option value="secretaire">Secrétaire</option>
                <option value="tresorier">Trésorier</option>
                <option value="membre">Membre</option>
              </select>
            </td>
            <td className="px-3 py-2">
              {availableLots.length > 0 ? (
                <select
                  value={membre.lotId || ''}
                  onChange={(e) => {
                    const selectedLot = availableLots.find((l) => l.id === e.target.value);
                    onUpdate(membre.tempId, 'lotId', e.target.value);
                    if (selectedLot) {
                      onUpdate(membre.tempId, 'lotNumero', selectedLot.numero);
                    }
                  }}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="">-- Sélectionner --</option>
                  {availableLots.map((lot) => (
                    <option key={lot.id} value={lot.id}>
                      Lot {lot.numero}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-sm text-gray-400 italic">Aucun lot</span>
              )}
            </td>
            <td className="px-3 py-2">
              <input
                type="email"
                value={membre.email || ''}
                onChange={(e) => onUpdate(membre.tempId, 'email', e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="email@exemple.fr"
              />
            </td>
            <td className="px-3 py-2 text-center">
              <button
                onClick={() => onDelete(membre.tempId)}
                className="text-red-500 hover:text-red-700"
                title="Supprimer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default ExtractionValidationModal;